import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Typography, Paper, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router';
import Footer from '../layout/Footer';

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionTimeoutMessage, setSessionTimeoutMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Check if redirected due to session timeout
    const reason = searchParams.get('reason');
    if (reason) {
      let message = '';
      switch (reason) {
        case 'token_expired':
          message = 'Your session has expired. Please log in again.';
          break;
        case 'session_expired':
          message = 'Your session has timed out. Please log in again.';
          break;
        case 'unauthorized':
          message = 'Authentication required. Please log in.';
          break;
        default:
          message = 'Your session has ended. Please log in again.';
      }
      setSessionTimeoutMessage(message);

      // Clear the reason from URL after showing the message
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('reason');
      navigate(`/login${newSearchParams.toString() ? `?${newSearchParams}` : ''}`, {
        replace: true,
      });
    }
  }, [searchParams, navigate]);

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Typography>Loading authentication status...</Typography>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: 400,
          mb: 'auto',
          mt: 'auto',
        }}
      >
        <Typography component="h1" variant="h4" gutterBottom>
          Reindeer Data Collection
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 4 }}>
          Sign in to access the web management console
        </Typography>

        {sessionTimeoutMessage && (
          <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
            {sessionTimeoutMessage}
          </Alert>
        )}

        <Button fullWidth variant="contained" color="primary" size="large" onClick={login}>
          Sign In
        </Button>
      </Paper>
      <Footer />
    </Box>
  );
};

export default Login;
